import { useState, useEffect, useRef } from "react";
import {
  FiX,
  FiMapPin,
  FiSearch,
  FiLoader,
  FiCheck
} from "react-icons/fi";

const MapEditModal = ({ show, onClose, onMapUpdate, currentAddress }) => {
  const [address, setAddress] = useState(currentAddress || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const searchTimeout = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setAddress(currentAddress || "");
      setSuggestions([]);
      setSelectedLocation(null);
      setPreviewUrl("");
      // Focus input when modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [show, currentAddress]);

  // Search for addresses using OpenStreetMap Nominatim
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'WebsiteBuilder/1.0'
          }
        }
      );
      const data = await response.json();
      setSuggestions(data);
    } catch (error) {
      console.error("Error searching address:", error);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle input change with debounce
  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    setSelectedLocation(null);

    // Debounce search
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      searchAddress(value);
    }, 300);
  };

  // Generate OpenStreetMap embed URL
  const generateMapEmbedUrl = (lat, lon, zoom = 15) => {
    // Using OpenStreetMap's embed
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lon - 0.01}%2C${lat - 0.01}%2C${lon + 0.01}%2C${lat + 0.01}&layer=mapnik&marker=${lat}%2C${lon}`;
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    const displayName = suggestion.display_name;
    const lat = parseFloat(suggestion.lat);
    const lon = parseFloat(suggestion.lon);

    setAddress(displayName);
    setSelectedLocation({
      address: displayName,
      lat,
      lon
    });
    setSuggestions([]);

    // Generate preview
    const embedUrl = generateMapEmbedUrl(lat, lon);
    setPreviewUrl(embedUrl);
  };

  // Handle confirm
  const handleConfirm = () => {
    if (selectedLocation && previewUrl) {
      onMapUpdate({
        address: selectedLocation.address,
        embedUrl: previewUrl,
        lat: selectedLocation.lat,
        lon: selectedLocation.lon
      });
      onClose();
    }
  };

  // Handle manual search (Enter key)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && suggestions.length > 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[0]);
    }
  };

  if (!show) return null;

  return (
    <div className="map-modal-overlay" onClick={onClose}>
      <div className="map-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="map-modal-header">
          <h3><FiMapPin /> Update Map Location</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="map-modal-content">
          {/* Address Search */}
          <div className="address-search-wrapper">
            <label>Search Address</label>
            <div className="address-input-container">
              <FiSearch className="search-icon" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Enter address or location..."
                value={address}
                onChange={handleAddressChange}
                onKeyDown={handleKeyDown}
              />
              {isSearching && <FiLoader className="loading-icon spin" />}
            </div>

            {/* Suggestions Dropdown */}
            {suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.place_id || index}
                    className="suggestion-item"
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    <FiMapPin className="suggestion-icon" />
                    <span>{suggestion.display_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Location Info */}
          {selectedLocation && (
            <div className="selected-location">
              <FiCheck className="check-icon" />
              <div>
                <strong>Selected Location:</strong>
                <p>{selectedLocation.address}</p>
                <small>Coordinates: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lon.toFixed(6)}</small>
              </div>
            </div>
          )}

          {/* Map Preview */}
          {previewUrl && (
            <div className="map-preview">
              <label>Preview</label>
              <div className="map-preview-frame">
                <iframe
                  src={previewUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="map-modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-confirm"
            onClick={handleConfirm}
            disabled={!selectedLocation}
          >
            <FiCheck /> Update Map
          </button>
        </div>
      </div>

      <style jsx="true">{`
        .map-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          padding: 20px;
        }

        .map-modal {
          background: #1f2937;
          border-radius: 16px;
          width: 100%;
          max-width: 650px;
          min-height: 550px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: visible;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }

        .map-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #374151;
        }

        .map-modal-header h3 {
          margin: 0;
          color: #fff;
          font-size: 18px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .modal-close-btn {
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .modal-close-btn:hover {
          background: #374151;
          color: #fff;
        }

        .map-modal-content {
          flex: 1;
          padding: 24px;
          min-height: 380px;
          display: flex;
          flex-direction: column;
        }

        .address-search-wrapper {
          position: relative;
          margin-bottom: 20px;
          z-index: 100;
          min-height: 80px;
        }

        .address-search-wrapper label {
          display: block;
          color: #9ca3af;
          font-size: 13px;
          margin-bottom: 8px;
        }

        .address-input-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .address-input-container input {
          width: 100%;
          padding: 12px 16px 12px 40px;
          background: #374151;
          border: 1px solid #4b5563;
          border-radius: 8px;
          color: #fff;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }

        .address-input-container input:focus {
          border-color: #7c3aed;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          color: #9ca3af;
        }

        .loading-icon {
          position: absolute;
          right: 14px;
          color: #7c3aed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .suggestions-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #374151;
          border: 1px solid #4b5563;
          border-radius: 8px;
          margin-top: 4px;
          max-height: 250px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
        }

        .suggestion-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px;
          cursor: pointer;
          transition: background 0.2s;
          border-bottom: 1px solid #4b5563;
        }

        .suggestion-item:last-child {
          border-bottom: none;
        }

        .suggestion-item:hover {
          background: #4b5563;
        }

        .suggestion-icon {
          color: #7c3aed;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .suggestion-item span {
          color: #e5e7eb;
          font-size: 13px;
          line-height: 1.4;
        }

        .selected-location {
          display: flex;
          gap: 12px;
          padding: 14px;
          background: rgba(124, 58, 237, 0.1);
          border: 1px solid rgba(124, 58, 237, 0.3);
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .check-icon {
          color: #10b981;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .selected-location strong {
          color: #fff;
          font-size: 13px;
          display: block;
          margin-bottom: 4px;
        }

        .selected-location p {
          color: #d1d5db;
          font-size: 13px;
          margin: 0 0 4px 0;
          line-height: 1.4;
        }

        .selected-location small {
          color: #9ca3af;
          font-size: 11px;
        }

        .map-preview {
          margin-top: 16px;
          flex: 1;
        }

        .map-preview label {
          display: block;
          color: #9ca3af;
          font-size: 13px;
          margin-bottom: 8px;
        }

        .map-preview-frame {
          height: 280px;
          border-radius: 8px;
          overflow: hidden;
          background: #374151;
        }

        .map-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid #374151;
          background: #111827;
        }

        .btn-cancel {
          padding: 10px 20px;
          background: transparent;
          border: 1px solid #4b5563;
          border-radius: 8px;
          color: #9ca3af;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #374151;
          color: #fff;
        }

        .btn-confirm {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: #7c3aed;
          border: none;
          border-radius: 8px;
          color: #fff;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-confirm:hover {
          background: #6d28d9;
        }

        .btn-confirm:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default MapEditModal;
